import { createPortal } from "react-dom";
import { getPlacement } from "../../../helper/utils";
import { PopoverCard } from "../../atoms/Popover";
import { useEffect, useState } from "react";
import { usePopper } from "react-popper";

const portalRoot = document.getElementById("root-modals");

/**
 * Creates a floating popover aligned to an anchor element using Popper.js.
 *
 * Params:
 * - anchorEl (HTMLElement|null): The DOM element to anchor the popover to. If null, popover is not shown.
 * - children (React.ReactNode): Content to render inside the popover.
 * - onClose (Function): Callback fired when clicking outside or pressing Escape.
 * - offset (Array): position offset of popover, default to [0, 8]
 * - ...props: Extra props to pass on
 *
 * Returns:
 * - React.ReactPortal|null: The rendered popover portal, or null if no anchor.
 */
function Popover({ anchorEl, children, onClose, offset = [0, 8], ...props }) {
  // State to hold the popper (floating) element reference
  const [popperEl, setPopperEl] = useState(null);

  // Compute placement direction on every render
  const placement = getPlacement(anchorEl);

  // Initialize Popper with offset, overflow prevention, and flip behavior
  const { styles, attributes, update } = usePopper(anchorEl, popperEl, {
    placement,
    modifiers: [
      { name: "offset", options: { offset: offset } },
      { name: "preventOverflow", options: { boundary: "viewport" } },
      { name: "flip", options: { fallbackPlacements: ["top-start", "top-end"] } },
      { name: "computeStyles", options: { adaptive: false } }, // stable positioning
    ],
  });

  // Recalculate position when dependencies change
  useEffect(() => {
    if (update) update();
  }, [anchorEl, children, popperEl, update]);

  // Close on outside click or Escape key
  useEffect(() => {
    if (!anchorEl) return;
    const handleClickOutside = event => {
      if (popperEl && !popperEl.contains(event.target)) onClose?.();
    };
    const handleEscape = event => {
      if (event.key === "Escape") onClose?.();
    };

    document.addEventListener("pointerdown", handleClickOutside, true);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("pointerdown", handleClickOutside, true);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [anchorEl, popperEl, onClose]);

  if (!portalRoot || !anchorEl) return null;

  // Render the popover in a portal
  return createPortal(
    <PopoverCard
      ref={setPopperEl}
      role="dialog"
      style={{ ...styles.popper }}
      {...attributes.popper}
      {...props}
    >
      {children}
    </PopoverCard>,
    portalRoot,
  );
}

export { Popover };
